import CircularProgress from '@/Components/CircularProgress/CircularProgress';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import { type FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

// Idea loosely based on observer() wrapper from MobX-react-lite
// https://github.com/mobxjs/mobx-react-lite/blob/master/src/useObserver.ts

// This is VERY similar to the useFilingCabinet hook, except we can now essentially BLOCK the rendering of the underlying component
// until we know the initial state of the component for sure before rendering

interface FilingCabinetPageProps {
    renderPage: ({ state }: { state: FilingCabinetItem }) => React.ReactElement;
    reuseFile?: boolean;
}

function FilingCabinetPage(props: Readonly<FilingCabinetPageProps>): React.ReactElement {
    const { reuseFile, renderPage } = props;

    const { generateNewID, getWithID, add, loadByPath } = useFilingCabinetContext();
    const { pathname, search } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const fcKey = searchParams.get('fc');
    let filingCabinetKey = fcKey != null ? Number.parseInt(fcKey) : undefined;
    const [pageState, setPageState] = useState<FilingCabinetItem | null>(null);

    const functionalPage = useMemo(() => {
        if (pageState === null) {
            return <></>;
        }
        return renderPage({ state: pageState });
    }, [pageState]);

    useEffect(() => {
        const fullPath = pathname + search; // We want to keep track of the FULL url with searchParams (without fc key), otherwise we lose some information to populate the page

        if (reuseFile && !filingCabinetKey) {
            const existingFCItemWithSamePath = loadByPath(pathname);
            const key = existingFCItemWithSamePath?.ID;
            if (key) {
                add({ ...existingFCItemWithSamePath, path: fullPath }); // Update the path of the existing item in case the URL parameters have changed (for example: when selecting a different saved filter for the worklist in the Menu)
                setSearchParams((prev) => {
                    const params = prev;
                    params.delete('fc');
                    params.append('fc', `${key}`);
                    return params;
                }, { replace: true });
                return;
            }
        }
        if (filingCabinetKey) {
            const item = getWithID(filingCabinetKey);
            // Only update pageState if the FC item ID changed (e.g. first load, or opening a different FC tab).
            // When navigating via the nav menu (no fc param), the effect re-fires after fc is appended
            // to the URL. If the FC item ID is the same, the existing page component and its stores are
            // still valid — no recreation needed.
            if (item && pageState?.ID !== filingCabinetKey) {
                setPageState(item);
            }
        }
        else {
            filingCabinetKey = generateNewID();
            const newItem: FilingCabinetItem = { ID: filingCabinetKey, path: fullPath, title: 'Loading', active: true, isLocked: false };
            add(newItem);
            setSearchParams((prev) => {
                const params = prev;
                params.delete('fc');
                params.append('fc', (filingCabinetKey!).toString());
                return params;
            }, { replace: true });
        }
    }, [filingCabinetKey]);

    // Once pageState is set, keep rendering the page to avoid unmounting the child
    // component tree (and losing MobX store state) during transient URL changes
    // (e.g. when reuseFile appends the fc param).
    if (pageState === null) {
        return <CircularProgress />;
    }
    return functionalPage;
}

export default FilingCabinetPage;
